import {
  KdsButton,
  KdsDropfile,
  KdsTextArea,
} from "@kazaar-platform/design-system";
import { RiAddCircleLine, RiIndeterminateCircleLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { isAxiosError } from "axios";
import clsx from "clsx";
import { useContext, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { FileContext } from "@/components/FileUploadAsync";
import { SIZE_5G } from "@/constants/fileSize.constants";
import { getDropfileErrorMessage } from "@/helpers/forms";
import { useDirectUpload } from "@/hooks/useDirectUpload";

import { type UploadProofItemProps } from "./UploadProofItem.types";

export const UploadProofItem = ({ index, onRemove }: UploadProofItemProps) => {
  const { t } = useTranslate();
  const methods = useFormContext();

  // States
  const [commentIsVisible, setCommentIsVisible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const { pushFile, setFileUploadState } = useContext(FileContext);
  const { uploadFile, createFileUploadUrl } = useDirectUpload();

  const controllerName = `attachments.${index}.fileSignedId`;

  // Handle Toggle Comment
  const toggleComment = (index: number) => {
    if (commentIsVisible) {
      setCommentIsVisible(false);
      methods.resetField(`attachments.${index}.comment`);
    } else {
      setCommentIsVisible(true);
    }
  };

  const handleUpload = async (file: File) => {
    try {
      const uploadDetails = await createFileUploadUrl(
        "orderItem.deliveryNotes",
        file
      );
      const newSignedId = uploadDetails?.fileSignedId;

      pushFile(newSignedId, {
        fileName: file.name,
        size: file.size,
        contentType: file.type,
      });

      await uploadFile(file, uploadDetails);

      setFileUploadState(newSignedId, "uploaded");

      methods.setValue(controllerName, newSignedId);
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 422) {
          setFileUploadState(error.response?.data.fileSignedId, "error");
          methods.setError(controllerName, {
            type: "manual",
            message: t("dropfile.errorDropfileType"),
          });
          methods.setValue(controllerName, undefined);
          setIsLoading(false);
        }
      }
    }
  };

  return (
    <div className="flex flex-col gap-sp-xs">
      <Controller
        name={controllerName}
        render={({ field, fieldState }) => (
          <KdsDropfile
            dataCy="fileUploadForProof"
            errorMessage={getDropfileErrorMessage(fieldState.error?.message)}
            hasError={!!fieldState.error}
            isDeletable={!!field.value}
            isReadable={!!field.value}
            isLoading={isLoading}
            maxSize={SIZE_5G}
            onDelete={() => field.onChange(undefined)}
            onUpload={(file) => void handleUpload(file)}
            onUploadError={() => methods.setValue(controllerName, undefined)}
            onUploadStart={() => {
              setIsLoading(true);
              methods.clearErrors(controllerName);
            }}
            onUploadSuccess={() => setIsLoading(false)}
            placeholderDescription={t("dropfile.placeholderMaxSize5GB")}
            placeholderTitle={t("dropfile.placeholderTitle")}
          />
        )}
      />

      <div className="flex flex-col items-end">
        <KdsButton
          onPress={() => toggleComment(index)}
          variant="ghost"
          iconLeft={
            commentIsVisible ? RiIndeterminateCircleLine : RiAddCircleLine
          }
          color="neutral"
        >
          {commentIsVisible
            ? t("buttons.deleteComment")
            : t("buttons.addComment")}
        </KdsButton>

        {commentIsVisible && (
          <div className="-mt-5 w-full animate-fade-in">
            <KdsTextArea
              label={t("inputs.comment.label")}
              placeholder={t("order.myOrdersItemDetails.commentPlaceholder")}
              // ToDo allow classname or min-h on textarea DS2
              // className="!min-h-[50px] text-sm"
              maxLength={4000}
              {...methods.register(`attachments.${index}.comment`, {
                required: false,
              })}
            />
          </div>
        )}
      </div>
      {index > 0 && (
        <div className={clsx("w-fit", commentIsVisible ? "mt-3" : "-mt-4")}>
          <KdsButton
            variant="outlined"
            size="medium"
            iconLeft={RiIndeterminateCircleLine}
            color="neutral"
            onPress={() => onRemove(index)}
          >
            {t("buttons.deleteProof")}
          </KdsButton>
        </div>
      )}
    </div>
  );
};
